import { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { IoCheckmarkCircle, IoCloseCircle, IoRefresh, IoServer, IoCodeSlash, IoNewspaper, IoMedkit } from "react-icons/io5";
import { useAnalytics } from "context/AnalyticsContext";

import GlassCard from "components/GlassCard";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import VuiBadge from "components/ui/VuiBadge";
import VuiButton from "components/ui/VuiButton";
import PorterTable from "components/PorterTable";

import { adminService } from "services/adminService";

const REFRESH_INTERVAL_MS = 60 * 1000; // 60 seconds

// ── Helpers ─────────────────────────────────────────────────────────────────

function daysAgo(dateStr) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function nextTueThu(afterHour) {
  const now = new Date();
  const parisDiff = 60; // CET offset in minutes (UTC+1); adjust if needed
  const paris = new Date(now.getTime() + parisDiff * 60 * 1000);
  const dayOfWeek = paris.getDay(); // 0=Sun, 2=Tue, 4=Thu
  const candidateDays = [2, 4]; // Tuesday, Thursday

  for (let add = 1; add <= 7; add++) {
    const candidate = (dayOfWeek + add) % 7;
    if (candidateDays.includes(candidate)) {
      const result = new Date(paris);
      result.setDate(paris.getDate() + add);
      result.setHours(afterHour, 0, 0, 0);
      return result.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) +
        " at " + String(afterHour).padStart(2, "0") + ":00";
    }
  }
  return "next Tue/Thu";
}

function schedulerBadge(days) {
  if (days === null) return { color: "warning", label: "Never ran" };
  if (days <= 4)      return { color: "success", label: "On track" };
  if (days <= 7)      return { color: "warning", label: "Overdue" };
  return               { color: "error",   label: "Missed" };
}

// ── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const isUp = status === "UP";
  return (
    <VuiBadge
      variant="standard"
      badgeContent={isUp ? "UP" : "DOWN"}
      color={isUp ? "success" : "error"}
      size="xs"
      container
    />
  );
}

function ServiceCard({ icon: Icon, label, status, detail }) {
  const loading = status === undefined;
  return (
    <GlassCard sx={{ height: "100%" }}>
      <VuiBox display="flex" flexDirection="column" gap={1.5} p={0.5}>
        <VuiBox display="flex" alignItems="center" gap={1}>
          <VuiBox sx={{ color: "rgba(255,255,255,0.7)", display: "flex" }}>
            <Icon size="20px" />
          </VuiBox>
          <VuiTypography variant="button" color="white" fontWeight="medium">
            {label}
          </VuiTypography>
        </VuiBox>

        <VuiBox display="flex" alignItems="center" gap={1}>
          {loading ? (
            <CircularProgress size={16} sx={{ color: "rgba(255,255,255,0.5)" }} />
          ) : (
            <>
              {status === "UP"
                ? <IoCheckmarkCircle size="18px" color="#4ade80" />
                : <IoCloseCircle size="18px" color="#f87171" />}
              <StatusBadge status={status} />
            </>
          )}
        </VuiBox>

        {detail && status === "DOWN" && (
          <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.7rem", wordBreak: "break-all" }}>
            {detail}
          </VuiTypography>
        )}
      </VuiBox>
    </GlassCard>
  );
}

/**
 * nextRunHour   — computes "next Tue/Thu at HH:00" (existing behaviour for bi-weekly scrapers)
 * nextRunText   — static label for custom schedules (e.g. "1st Saturday of each month at 10:00")
 * Pass one or the other; nextRunText takes precedence.
 */
function SchedulerCard({ label, icon: Icon, lastRunDate, nextRunHour, nextRunText }) {
  const days = daysAgo(lastRunDate);
  const badge = schedulerBadge(days);
  const nextRun = nextRunText || nextTueThu(nextRunHour);

  return (
    <GlassCard sx={{ height: "100%" }}>
      <VuiBox display="flex" flexDirection="column" gap={1.5} p={0.5}>
        <VuiBox display="flex" alignItems="center" justifyContent="space-between">
          <VuiBox display="flex" alignItems="center" gap={1}>
            <VuiBox sx={{ color: "rgba(255,255,255,0.7)", display: "flex" }}>
              <Icon size="18px" />
            </VuiBox>
            <VuiTypography variant="button" color="white" fontWeight="medium">
              {label}
            </VuiTypography>
          </VuiBox>
          <VuiBadge variant="standard" badgeContent={badge.label} color={badge.color} size="xs" container />
        </VuiBox>

        <VuiBox display="flex" flexDirection="column" gap={0.5}>
          <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.75rem" }}>
            Last run:{" "}
            <span style={{ color: "#fff", fontWeight: 500 }}>
              {lastRunDate
                ? `${new Date(lastRunDate).toLocaleDateString("en-GB")} (${days === 0 ? "today" : `${days}d ago`})`
                : "Never"}
            </span>
          </VuiTypography>
          <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.75rem" }}>
            Next scheduled:{" "}
            <span style={{ color: "rgba(255,255,255,0.85)" }}>{nextRun}</span>
          </VuiTypography>
        </VuiBox>
      </VuiBox>
    </GlassCard>
  );
}

function ActionButton({ label, onClick, loading, result }) {
  return (
    <VuiBox display="flex" flexDirection="column" gap={0.5} flex={1}>
      <VuiButton
        color="info"
        variant="outlined"
        size="small"
        onClick={onClick}
        disabled={loading}
        fullWidth
        sx={{ minHeight: "36px" }}
      >
        {loading ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : label}
      </VuiButton>
      {result && (
        <VuiTypography
          variant="caption"
          sx={{
            fontSize: "0.7rem",
            color: result.ok ? "#4ade80" : "#f87171",
            textAlign: "center",
          }}
        >
          {result.message}
        </VuiTypography>
      )}
    </VuiBox>
  );
}

// ── Scrapers table columns ───────────────────────────────────────────────────

const scraperColumns = [
  { key: "name",    name: "Name",    align: "left",   width: "200px" },
  { key: "type",    name: "Type",    align: "left",   width: "180px" },
  { key: "version", name: "Version", align: "center", width: "120px" },
  {
    key: "isActive",
    name: "Status",
    align: "center",
    width: "100px",
    render: (val) => (
      <VuiBadge
        variant="standard"
        badgeContent={val ? "Active" : "Inactive"}
        color={val ? "success" : "error"}
        size="xs"
        container
      />
    ),
  },
];

// ── Main Component ───────────────────────────────────────────────────────────

function SystemMonitor() {
  const { companyId, competitors } = useAnalytics();
  const [health, setHealth]             = useState(null);
  const [lastUpdate, setLastUpdate]     = useState(null);
  const [scrapers, setScrapers]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const [actionLoading, setActionLoading] = useState({});
  const [actionResult, setActionResult]   = useState({});

  const fetchAll = useCallback(async () => {
    try {
      const [healthRes, updateRes, scrapersRes] = await Promise.allSettled([
        adminService.getSystemHealth(),
        adminService.getLastUpdateDate(),
        adminService.getAllScrapers(),
      ]);

      if (healthRes.status === "fulfilled")   setHealth(healthRes.value.data);
      if (updateRes.status === "fulfilled")   setLastUpdate(updateRes.value.data);
      if (scrapersRes.status === "fulfilled") setScrapers(scrapersRes.value.data || []);

      setLastRefreshed(new Date());
    } catch (_) {
      // individual failures handled per-promise above
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const timer = setInterval(fetchAll, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchAll]);

  const runAction = useCallback(async (key, fn, successMsg) => {
    setActionLoading((p) => ({ ...p, [key]: true }));
    setActionResult((p) => ({ ...p, [key]: null }));
    try {
      await fn();
      setActionResult((p) => ({ ...p, [key]: { ok: true, message: successMsg } }));
    } catch (e) {
      setActionResult((p) => ({ ...p, [key]: { ok: false, message: e?.response?.data?.message || "Failed" } }));
    } finally {
      setActionLoading((p) => ({ ...p, [key]: false }));
    }
  }, []);

  const h = health || {};
  const bc   = h.bridgeCollector;
  const ns   = h.newsScrapers;
  const llm  = h.llmAnalyzer;

  return (
    <VuiBox>
      {/* Header row */}
      <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <VuiTypography variant="h6" color="white" fontWeight="medium">
          System Monitor
        </VuiTypography>
        <VuiBox display="flex" alignItems="center" gap={1}>
          {lastRefreshed && (
            <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.7rem" }}>
              Last refreshed: {lastRefreshed.toLocaleTimeString()}
            </VuiTypography>
          )}
          <VuiButton
            color="secondary"
            variant="outlined"
            size="small"
            onClick={fetchAll}
            disabled={loading}
            sx={{ minWidth: "auto", px: 1, py: 0.5 }}
          >
            <IoRefresh size="14px" />
          </VuiButton>
        </VuiBox>
      </VuiBox>

      {/* Row 1 — Service Health */}
      <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.7rem" }} mb={1} display="block">
        Service Health
      </VuiTypography>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <ServiceCard icon={IoServer}   label="bridge-collector" status={bc?.status}  detail={bc?.detail} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ServiceCard icon={IoNewspaper} label="news-scrapers"    status={ns?.status}  detail={ns?.detail} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ServiceCard icon={IoCodeSlash} label="llm-analyzer"     status={llm?.status} detail={llm?.detail} />
        </Grid>
      </Grid>

      {/* Row 2 — Scheduler Status */}
      <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.7rem" }} mb={1} display="block">
        Scheduled Pipelines
      </VuiTypography>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <SchedulerCard
            icon={IoNewspaper}
            label="News Scraper"
            lastRunDate={lastUpdate?.NEWS}
            nextRunHour={22}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SchedulerCard
            icon={IoServer}
            label="LinkedIn Scraper"
            lastRunDate={lastUpdate?.LINKEDIN_POSTS}
            nextRunHour={23}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SchedulerCard
            icon={IoCodeSlash}
            label="Clinical Trials Scraper"
            lastRunDate={lastUpdate?.CLINICAL_TRIALS}
            nextRunText="1st Saturday of each month at 10:00"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SchedulerCard
            icon={IoMedkit}
            label="Product Showdown (FDA)"
            lastRunDate={lastUpdate?.FDA_DEVICES}
            nextRunText="15th of each month at 06:00"
          />
        </Grid>
      </Grid>

      {/* Row 3 — Apify Scrapers */}
      <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.7rem" }} mb={1} display="block">
        Apify Scraper Actors
      </VuiTypography>
      <GlassCard sx={{ mb: 3 }}>
        <PorterTable
          columns={scraperColumns}
          rows={scrapers}
          maxHeight="320px"
          emptyMessage="No scraper actors found."
        />
      </GlassCard>

      {/* Row 4 — Manual Actions */}
      <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.7rem" }} mb={1} display="block">
        Manual Actions
      </VuiTypography>
      <GlassCard>
        <VuiBox display="flex" flexWrap="wrap" gap={2} alignItems="flex-start">
          <ActionButton
            label="Run News Scrapers"
            loading={!!actionLoading.news}
            result={actionResult.news}
            onClick={() => runAction("news", adminService.triggerNewsScraper, "News scraping started")}
          />
          <ActionButton
            label="Run LinkedIn Scrapers"
            loading={!!actionLoading.linkedin}
            result={actionResult.linkedin}
            onClick={() => runAction("linkedin", adminService.triggerLinkedInScraper, "LinkedIn scraping started")}
          />
          <ActionButton
            label="Clear News Caches"
            loading={!!actionLoading.clearNews}
            result={actionResult.clearNews}
            onClick={() => runAction("clearNews", () => adminService.clearCacheBySource("NEWS"), "News caches cleared")}
          />
          <ActionButton
            label="Clear LinkedIn Caches"
            loading={!!actionLoading.clearLinkedIn}
            result={actionResult.clearLinkedIn}
            onClick={() => runAction("clearLinkedIn", () => adminService.clearCacheBySource("LINKEDIN_POSTS"), "LinkedIn caches cleared")}
          />
          <ActionButton
            label="Run Jobs Scraper"
            loading={!!actionLoading.jobs}
            result={actionResult.jobs}
            onClick={() => runAction("jobs", adminService.triggerJobsScraper, "Jobs scraping started")}
          />
          <ActionButton
            label="Reclassify Jobs"
            loading={!!actionLoading.reclassify}
            result={actionResult.reclassify}
            onClick={() => runAction("reclassify", adminService.reclassifyJobs, "Jobs reclassified")}
          />
          <ActionButton
            label="Run Clinical Trials Scraper"
            loading={!!actionLoading.clinicalTrials}
            result={actionResult.clinicalTrials}
            onClick={() => runAction("clinicalTrials", adminService.triggerClinicalTrialsScraper, "Clinical trials scraping started")}
          />
          <ActionButton
            label="Scrape FDA Devices"
            loading={!!actionLoading.productShowdownScrape}
            result={actionResult.productShowdownScrape}
            onClick={() => runAction("productShowdownScrape", adminService.triggerProductShowdownScraper, "FDA device scrape started")}
          />
          <ActionButton
            label="Extract PDFs"
            loading={!!actionLoading.productShowdownPdfs}
            result={actionResult.productShowdownPdfs}
            onClick={() => runAction("productShowdownPdfs", adminService.triggerProductShowdownPdfExtraction, "PDF extraction started")}
          />
          <ActionButton
            label="Scrape KOL Posts"
            loading={!!actionLoading.kolScrape}
            result={actionResult.kolScrape}
            onClick={() => runAction("kolScrape", adminService.triggerKolScraper, "KOL scraping started")}
          />
          <ActionButton
            label="Classify KOL Posts"
            loading={!!actionLoading.kolClassify}
            result={actionResult.kolClassify}
            onClick={() => runAction("kolClassify", adminService.triggerKolAnalysis, "KOL classification started")}
          />
          <ActionButton
            label="Generate KOL Report"
            loading={!!actionLoading.kolReport}
            result={actionResult.kolReport}
            onClick={() => runAction("kolReport", () => adminService.triggerKolReport(companyId), "KOL report generation started")}
          />
        </VuiBox>
      </GlassCard>

      {/* Competitor + Market Reports — single regenerate-all button */}
      <GlassCard sx={{ mt: 3 }}>
        <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.7rem" }}>
            Competitor &amp; Market Reports
          </VuiTypography>
          {actionResult.allReports && (
            <VuiTypography variant="caption" sx={{ fontSize: "0.7rem", color: actionResult.allReports.ok ? "#4ade80" : "#f87171" }}>
              {actionResult.allReports.message}
            </VuiTypography>
          )}
        </VuiBox>
        <ActionButton
          label="Regenerate All Reports"
          loading={!!actionLoading.allReports}
          result={null}
          onClick={async () => {
            if (!competitors?.length) return;
            setActionLoading((p) => ({ ...p, allReports: true }));
            setActionResult((p) => ({ ...p, allReports: null }));
            let failed = 0;
            for (const c of competitors) {
              try {
                await adminService.regenerateCompetitorReport(c.id, companyId);
              } catch (_) { failed++; }
            }
            try {
              await adminService.regenerateMarketReport(companyId);
            } catch (_) { failed++; }
            setActionLoading((p) => ({ ...p, allReports: false }));
            const total = competitors.length + 1;
            setActionResult((p) => ({
              ...p,
              allReports: failed === 0
                ? { ok: true,  message: `All ${total} reports regenerated` }
                : { ok: false, message: `${total - failed}/${total} succeeded` },
            }));
          }}
        />
      </GlassCard>
    </VuiBox>
  );
}

export default SystemMonitor;
